import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
 FormGroup, FormControl, Button, Row, Col, Glyphicon
} from 'react-bootstrap';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ReusableTable from '../tables/ReusableTable.jsx';
import ActionCellRenderer from '../tables/ActionCellRenderer.jsx';
import validation from '../../lib/validation';

import { addToGroup, updateInGroup, removeFromGroup } from '../../actions/form';

export class HistoryForm extends Component {
  constructor(props) {
    super(props);

    /* istanbul ignore next */
    this.state = {
      inputs: {
        raceTeam: '',
        shopAddress: '',
        owner: '',
        spotterName: '',
        crewChief: '',
        seriesList: '',
      },
      edit: false,
      rules: {},
    };

    this.columnDefs = [
      { headerName: 'Team', field: 'raceTeam', minWidth: 200 },
      { headerName: 'Owner', field: 'owner', minWidth: 200 },
      { headerName: 'Location', field: 'shopAddress', minWidth: 200 },
      { headerName: 'Spotter', field: 'spotterName', minWidth: 200 },
      { headerName: 'Crew Chief', field: 'crewChief', minWidth: 200 },
      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 100,
        cellRendererFramework: ActionCellRenderer,
      },
    ];

    this.onInputChange = this.onInputChange.bind(this);
    this.resetFields = this.resetFields.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.editHandler = this.editHandler.bind(this);
    this.getValidationStates = this.getValidationStates.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.validateField = this.validateField.bind(this);
  }

  componentDidUpdate() {
    if (this.props.rules && !_.isEqual(this.props.rules, this.state.rules)) {
      this.setState({ rules: this.props.rules });
    }
  }

  onCancel() {
    this.resetFields();
    if (this.state.edit) this.cancelEdit();
  }

  validateField(field) {
    const fieldToValidate = this.state.inputs[field];
    return validation.run(_.get(this.state, `rules.${field}`), fieldToValidate);
  }

  validateAll() {
    const fields = ['raceTeam', 'shopAddress', 'owner', 'spotterName', 'crewChief', 'seriesList'];
    return _.every(fields, this.validateField);
  }

  onAdd() {
    if (!this.validateAll()) return;

    const recordToAdd = {
      raceTeam: this.state.inputs.raceTeam,
      shopAddress: this.state.inputs.shopAddress,
      owner: this.state.inputs.owner,
      spotterName: this.state.inputs.spotterName,
      crewChief: this.state.inputs.crewChief,
      seriesList: this.state.inputs.seriesList,
    };

    this.props.addToGroup('histories', recordToAdd);

    this.resetFields();
    this.setState({ edit: false });
  }

  onInputChange(e, field) {
    const updates = _.cloneDeep(this.state);
    updates.inputs[field] = e.target.value;
    this.setState(updates);
  }

  getValidationStates() {
    const defaultHistoryObj = {
      raceTeam: null,
      shopAddress: null,
      owner: null,
      spotterName: null,
      crewChief: null,
      seriesList: null,
    };

    return _.reduce(this.state.inputs, (acc, value, key) => {
      if (value !== '') acc[key] = this.validateField('raceTeam') ? 'success' : 'error'; // eslint-disable-line no-param-reassign
      return acc;
    }, defaultHistoryObj);
  }

  cancelEdit() {
    this.props.addToGroup('histories', this.state.edit);
    this.resetFields();
    this.setState({ edit: false });
  }

  editHandler(index) {
    const history = this.props.histories[index];
    if (!history) {
      // Add alert modal here
      return;
    }

    // Save a copy of the history we're editing and populate input fields
    const updates = _.cloneDeep(this.state);
    updates.edit = history; // store history to retrieve in case of 'cancel'
    updates.inputs.raceTeam = history.raceTeam;
    updates.inputs.shopAddress = history.shopAddress;
    updates.inputs.owner = history.owner;
    updates.inputs.spotterName = history.spotterName;
    updates.inputs.crewChief = history.crewChief;
    updates.inputs.seriesList = history.seriesList;

    this.setState(updates);
    this.props.removeFromGroup('histories', index);
  }

  resetFields() {
    const updates = _.cloneDeep(this.state);
    updates.inputs = {
      raceTeam: '',
      shopAddress: '',
      owner: '',
      spotterName: '',
      crewChief: '',
      seriesList: '',
    };
    this.setState(updates);
  }

  render() {
    const validationStates = this.getValidationStates();
    let addButton;
    if (this.state.edit) {
      addButton = (
        <Button onClick={this.onAdd}>
          <Glyphicon glyph="pencil" /> Update Team
        </Button>
      );
    } else {
      addButton = (
        <Button onClick={this.onAdd}>
          <Glyphicon glyph="plus" /> Add Team
        </Button>
      );
    }

    return (
      <div className="container-fluid form-container">
        <Row>
          <Col lg={12} className="form-title-container">
            <h1>{this.props.currentUserLevel}</h1>
            <span className="form-title-span">{this.props.name}</span>
          </Col>
        </Row>
        <form id="history-form" autoComplete="off">
          <Row>
            <Col sm={6}>
              <FormGroup validationState={validationStates.raceTeam} controlId="raceTeamInput">
                <FormControl
                  type="text"
                  value={this.state.inputs.raceTeam}
                  placeholder="Race Team"
                  name="raceTeam"
                  onChange={e => this.onInputChange(e, 'raceTeam')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup validationState={validationStates.shopAddress} controlId="shopAddressInput">
                <FormControl
                  type="text"
                  name="shopAddress"
                  value={this.state.inputs.shopAddress}
                  placeholder="Location (e.g. Charlotte, NC)"
                  onChange={e => this.onInputChange(e, 'shopAddress')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FormGroup validationState={validationStates.seriesList} controlId="seriesListTextarea">
                <FormControl
                  type="text"
                  name="seriesList"
                  value={this.state.inputs.seriesList}
                  placeholder="Series"
                  onChange={e => this.onInputChange(e, 'seriesList')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup validationState={validationStates.owner} controlId="ownerInput">
                <FormControl
                  type="text"
                  name="owner"
                  value={this.state.inputs.owner}
                  placeholder="Owner"
                  onChange={e => this.onInputChange(e, 'owner')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FormGroup validationState={validationStates.crewChief} controlId="crewChiefInput">
                <FormControl
                  type="text"
                  name="crewChief"
                  value={this.state.inputs.crewChief}
                  placeholder="Crew Chief"
                  onChange={e => this.onInputChange(e, 'crewChief')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup validationState={validationStates.spotterName} controlId="spotterNameInput">
                <FormControl
                  type="text"
                  name="spotterName"
                  value={this.state.inputs.spotterName}
                  placeholder="Spotter"
                  onChange={e => this.onInputChange(e, 'spotterName')}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <div className="form-control-btn-container pull-right">
              <Button onClick={this.onCancel} style={{ marginRight: '5px' }}>
                <Glyphicon glyph="remove" /> Cancel
              </Button>
              {addButton}
            </div>
          </Row>
          <Row>
            <ReusableTable
              histories={this.props.histories}
              columnDefs={this.columnDefs}
              editHandler={this.editHandler}
            />
          </Row>
        </form>
      </div>
    );
  }
}

export const mapStateToProps = state => ({
  histories: _.get(state, 'form.profile.fields.histories', []),
});

export default connect(
  mapStateToProps,
  { addToGroup, updateInGroup, removeFromGroup },
)(HistoryForm);

HistoryForm.propTypes = {
  name: PropTypes.string,
};
